<template>
  <div class="inbox-container" :class="{ 'is-resizing': isResizing, 'is-resizing-right': isResizingRightPanel, 'right-panel-visible': showRightPanel, 'left-sidebar-hidden': !showLeftSidebar }">
    <!-- Overlay for force-shown panels -->
    <div
      v-if="forceShowLeftSidebar || forceShowRightPanel"
      class="panel-overlay"
      @click="closeForcedPanels"
    ></div>

    <!-- Floating button to show left sidebar when folded -->
    <el-button
      v-if="false"
      class="floating-sidebar-toggle left"
      icon="el-icon-s-unfold"
      circle
      type="primary"
      @click="showLeftSidebar = true; forceShowLeftSidebar = true"
      title="Show message list"
    />

    <!-- Part A: Left Sidebar - List with Tabs (collapsible) -->
    <div v-if="true" class="inbox-sidebar" :class="{ 'force-show': forceShowLeftSidebar }" :style="{ width: sidebarWidth + 'px' }">
      <div class="resize-handle" @mousedown="startResize"></div>
      <!-- Close button when force-shown -->
      <el-button
        v-if="forceShowLeftSidebar"
        class="panel-close-btn"
        icon="el-icon-close"
        circle
        size="small"
        @click="closeForcedPanels"
        title="Hide sidebar"
      />

      <!-- Message List -->
      <div class="inbox-content">
        <div class="inbox-header">
          <div class="inbox-title-wrapper">
            <h2>Messages</h2>
            <el-badge :value="unreadCount" :hidden="!unreadCount" class="tab-unread-badge" />
          </div>
          <el-button
            class="collapse-btn"
            icon="el-icon-s-fold"
            circle
            size="small"
            @click="showLeftSidebar = false"
            title="Hide message list"
          />
        </div>
          <!-- Filter tags -->
          <div class="message-filter">
            <el-tag
              :type="messageFilter === 'all' ? 'primary' : 'info'"
              size="small"
              :effect="messageFilter === 'all' ? 'dark' : 'plain'"
              @click="messageFilter = 'all'"
              class="filter-tag"
            >
              All
            </el-tag>
            <el-tag
              :type="messageFilter === 'unread' ? 'primary' : 'info'"
              size="small"
              :effect="messageFilter === 'unread' ? 'dark' : 'plain'"
              @click="messageFilter = 'unread'"
              class="filter-tag"
            >
              Unread
            </el-tag>
            <el-button
              type="text"
              icon="el-icon-refresh"
              @click="refreshMessages"
              :loading="isRefreshing"
              size="small"
              class="refresh-btn"
            />
          </div>
          <div class="message-list">
            <!-- Load More Backward button at top -->
            <div v-if="!isLoadingMessages && messages.length > 0 && hasMoreMessagesBackward" class="load-more-container load-more-top">
              <el-button
                @click="loadMoreMessagesBackward"
                :loading="isLoadingMoreMessagesBackward"
                size="small"
                type="text"
              >
                Load Earlier Messages
              </el-button>
            </div>

            <div v-if="isLoadingMessages" class="loading-container">
              <i class="el-icon-loading"></i>
              <span>Loading messages...</span>
            </div>

            <div v-else-if="messages.length === 0" class="empty-state">
              <i class="el-icon-message" style="font-size: 48px; color: #909399;"></i>
              <p>No messages yet</p>
            </div>

            <div
              v-else
              v-for="message in filteredMessages"
              :key="message._id?.$oid || message._id?.$oid || message._id?.$id || message.id"
              :data-message-id="message._id?.$oid || message._id?.$oid || message._id?.$id || message.id"
              :class="['message-item', {
                'active': layoutMode === 'message-view' && selectedMessageId === (message._id?.$oid || message._id?.$oid || message._id?.$id || message.id),
                'unread': !message.receiver_read_status
              }]"
              @click="selectMessage(message)"
            >
              <div class="message-item-header">
                <el-avatar
                  :size="40"
                  :src="message.sender?.user_avatar_address_small"
                  :style="{
                    border: getBorderStyleByUserType(message.sender?.user_type),
                    backgroundColor: isValidAvatar(message.sender?.user_avatar_address_small) ? 'white' : '#e0e0e0',
                    color: '#409EFF',
                    flexShrink: 0
                  }"
                >
                  {{ isValidAvatar(message.sender?.user_avatar_address_small) ? '' : getInitial(message.sender?.user_nickName) }}
                </el-avatar>
                <div class="message-info">
                  <div class="message-header-row">
                    <div class="sender-name">{{ message.sender?.user_nickName || 'Unknown' }}</div>
                    <div class="message-meta">
                      <span class="message-time">{{ formatMessageTime(message.send_time) }}</span>
                      <el-badge v-if="!message.receiver_read_status" is-dot class="unread-indicator" />
                    </div>
                  </div>
                  <div class="message-preview">{{ getMessagePreview(message) }}</div>
                </div>
              </div>
            </div>
            <!-- Load More Forward button at bottom -->
            <div v-if="!isLoadingMessages && messages.length > 0 && hasMoreMessagesForward" class="load-more-container">
              <el-button
                @click="loadMoreMessagesForward"
                :loading="isLoadingMoreMessagesForward"
                size="small"
                type="text"
              >
                Load Later Messages
              </el-button>
            </div>
          </div>
      </div>
    </div>

    <!-- Part B: Message Detail Panel (right side) -->
    <div
      v-if="selectedMessage"
      class="middle-panel"
    >
      <div class="message-detail" :key="(selectedMessage._id?.$oid || selectedMessage._id?.$id || selectedMessage.id)">
        <!-- Message header -->
        <div class="message-detail-header">
          <div class="sender-info">
            <el-avatar
              :size="40"
              :src="selectedMessage.sender?.user_avatar_address_small"
              :style="{
                border: getBorderStyleByUserType(selectedMessage.sender?.user_type),
                backgroundColor: isValidAvatar(selectedMessage.sender?.user_avatar_address_small) ? 'white' : '#e0e0e0',
                color: '#409EFF',
                marginRight: '12px',
                flexShrink: 0
              }"
            >
              {{ isValidAvatar(selectedMessage.sender?.user_avatar_address_small) ? '' : getInitial(selectedMessage.sender?.user_nickName) }}
            </el-avatar>
            <div>
              <h3>{{ selectedMessage.sender?.user_nickName || 'Unknown' }}</h3>
              <span class="message-timestamp">{{ formatFullTime(selectedMessage.send_time) }}</span>
            </div>
          </div>
        </div>

        <!-- Message content -->
        <div class="message-detail-content">
          <!-- Card share notification (viewer is not creator) -->
          <div v-if="selectedMessage.message_content_type === 'card_share_notification'" class="card-share-content" style="display: flex; flex-direction: column;">
            <div v-if="selectedMessage.content" class="message-content-text" style="max-width: 700px; width: 100%; margin: 0 auto 16px auto; padding: 0 24px;">
              {{ selectedMessage.content }}
            </div>
            <DirectReadCard
              v-if="selectedMessageCardData"
              :card="selectedMessageCardData"
              :collections="[]"
              :showAnswer="true"
              :showReminder="false"
            />
            <div v-else class="loading-card">
              <i class="el-icon-loading"></i>
              <span>Loading card...</span>
            </div>
          </div>

          <!-- Card remind notification (viewer is creator) -->
          <div v-else-if="selectedMessage.message_content_type === 'card_remind_notification' || selectedMessage.message_type === 'card_remind_notification'" class="card-share-content" style="display: flex; flex-direction: column;">
            <div v-if="selectedMessage.content" class="message-content-text" style="max-width: 700px; width: 100%; margin: 0 auto 16px auto; padding: 0 24px;">
              {{ selectedMessage.content }}
            </div>
            <DirectReadCard
              v-if="selectedMessageCardData"
              :card="selectedMessageCardData"
              :collections="[]"
              :showAnswer="true"
              :showReminder="true"
            />
            <div v-else class="loading-card">
              <i class="el-icon-loading"></i>
              <span>Loading card...</span>
            </div>
          </div>

          <!-- Generative content notification (post) -->
          <div v-else-if="selectedMessage.message_content_type === 'generative_content_notification'" class="post-share-content">
            <div v-if="selectedMessage.content" class="message-content-text">
              {{ selectedMessage.content }}
            </div>
            <PostPreview
              v-if="selectedMessagePostData"
              :postTitle="selectedMessagePostData.title"
              :quizCount="selectedMessagePostData.quiz_count"
              :groupIndex="selectedMessagePostData.group_index"
              :collectionTitle="selectedMessagePostData.collection_title"
              :groupTitle="selectedMessagePostData.group_title"
              :postData="selectedMessagePostData"
            />
            <DirectReadPost
              v-if="selectedMessagePostData"
              :post="selectedMessagePostData"
            />
            <div v-else class="loading-post">
              <i class="el-icon-loading"></i>
              <span>Loading post...</span>
            </div>
          </div>

          <!-- Regular text message -->
          <div v-else class="message-content-text">
            {{ selectedMessage.content }}
          </div>
        </div>

        <!-- Message navigation buttons -->
        <div class="message-navigation">
          <el-button
            v-if="canNavigateToPreviousMessage"
            class="nav-btn-up"
            icon="el-icon-arrow-up"
            type="info"
            size="small"
            @click="navigateToPreviousMessage"
            title="Previous message"
          />
          <el-button
            v-if="canNavigateToNextMessage"
            class="nav-btn-down"
            icon="el-icon-arrow-down"
            type="info"
            size="small"
            @click="navigateToNextMessage"
            title="Next message"
          />
        </div>
      </div>
    </div>

    <!-- Floating button to show right panel when folded -->
    <el-button
      v-if="false"
      class="floating-sidebar-toggle right"
      icon="el-icon-s-unfold"
      circle
      type="primary"
      @click="forceShowRightPanel = true; showRightPanel = true"
      title="Show conversation"
    />

    <!-- Part C: Conversation Message List -->
    <!-- When message is clicked (rightmost, collapsible) -->
    <div
      v-if="false"
      :class="['content-panel', 'conversation-panel', 'right-panel', 'secondary-panel', { 'force-show': forceShowRightPanel }]"
      :style="{ width: rightPanelWidth + 'px' }"
    >
      <div v-if="showRightPanel" class="resize-handle-right" @mousedown.prevent="startRightPanelResize"></div>
      <!-- Close button when force-shown -->
      <el-button
        v-if="forceShowRightPanel && showRightPanel"
        class="panel-close-btn"
        icon="el-icon-close"
        circle
        size="small"
        @click="closeForcedPanels"
        title="Hide conversation"
      />
      <!-- Expand button when collapsed -->
      <div v-if="!showRightPanel" class="expand-btn" @click="showRightPanel = true">
        <i class="el-icon-d-arrow-left"></i>
      </div>
      <div class="panel-content" v-if="showRightPanel">
        <!-- Message detail content will be shown here -->
      </div>
    </div>

    <!-- Empty state when no message selected -->
    <div v-if="!selectedMessage" class="empty-content empty-detail-placeholder">
      <i class="el-icon-message" style="font-size: 64px; color: #c0c4cc;"></i>
      <p style="color: #909399; font-size: 16px;">Select a message to read</p>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import { getUserId, getUserName } from '@/utils/store'
import marked from 'marked'
import SimpleCardView from '@/components/SimpleCardView.vue'
import DirectReadCard from '@/components/DirectReadCard.vue'
import CardPreview from '@/components/CardPreview.vue'
import PostPreview from '@/components/PostPreview.vue'
import DirectReadPost from '@/components/DirectReadPost.vue'

export default {
  name: 'Inbox',
  components: {
    SimpleCardView,
    DirectReadCard,
    CardPreview,
    PostPreview,
    DirectReadPost
  },
  data() {
    return {
      // Message data
      messages: [],
      selectedMessage: null,
      selectedMessageId: null,
      messageFilter: 'all', // 'all' or 'unread'

      // Layout state
      layoutMode: 'message-view',
      showLeftSidebar: true, // Collapsible state for left sidebar
      showRightPanel: false, // Collapsible state for rightmost panel

      // Loading states
      isLoadingMessages: false,
      isRefreshing: false,
      unreadCount: 0,

      // Pagination for messages (direction-based)
      messagesLimit: 10,
      hasMoreMessagesForward: true,
      hasMoreMessagesBackward: true,
      isLoadingMoreMessagesForward: false,
      isLoadingMoreMessagesBackward: false,

      // Sidebar resize
      sidebarWidth: 350,
      isResizing: false,
      resizeStartX: 0,
      resizeStartWidth: 0,

      // Right panel resize
      rightPanelWidth: 400,
      isResizingRightPanel: false,
      rightPanelResizeStartX: 0,
      rightPanelResizeStartWidth: 0,

      // Card data for card share notifications
      selectedMessageCardData: null,

      // Post data for generative content notifications
      selectedMessagePostData: null,

      // Force show panels on mobile
      forceShowLeftSidebar: false,
      forceShowRightPanel: false,

      // Track window width for responsive behavior
      windowWidth: window.innerWidth,

      // Track navigation direction for auto-skip
      lastNavigationDirection: null, // 'up' or 'down'
      
      // Interested questions data
      interestedQuestions: [],
      isLoadingInterestedQuestions: false,
      isLoadingMoreQuestions: false,
      hasMoreQuestions: true,
      questionsOffset: 0,
      questionsLimit: 10,
      
      // Selected question for detail view
      selectedQuestion: null,
      showQuestionDetail: false
    }
  },
  computed: {
    filteredMessages() {
      if (this.messageFilter === 'unread') {
        return this.messages.filter(msg => !msg.receiver_read_status)
      }
      return this.messages
    },
    isRightPanelHiddenByResponsive() {
      return this.windowWidth < 1000
    },
    isLeftSidebarHiddenByResponsive() {
      return this.windowWidth < 780
    },
    // Message navigation for middle panel (message list)
    currentMessageIndex() {
      if (!this.selectedMessage) return -1
      const messageId = this.selectedMessage._id?.$oid || this.selectedMessage._id?.$id || this.selectedMessage.id
      return this.messages.findIndex(m => (m._id?.$oid || m._id?.$oid || m._id?.$id || m.id) === messageId)
    },
    canNavigateToPreviousMessage() {
      // Always show if there's a selected message
      return this.currentMessageIndex >= 0
    },
    canNavigateToNextMessage() {
      // Always show if there's a selected message
      return this.currentMessageIndex >= 0
    }
  },
  watch: {
    async selectedMessage(newMessage, oldMessage) {
      console.log('=== selectedMessage WATCHER TRIGGERED ===')
      const oldId = oldMessage ? (oldMessage._id?.$oid || oldMessage._id?.$id || oldMessage.id) : null
      const newId = newMessage ? (newMessage._id?.$oid || newMessage._id?.$id || newMessage.id) : null

      console.log('OLD message:', oldMessage ? {
        id: oldId,
        title: oldMessage.title,
        message_content_type: oldMessage.message_content_type,
        card_id: oldMessage.card_id || oldMessage.content_id || oldMessage.message_content_params?.card_id
      } : 'NULL')
      console.log('NEW message:', newMessage ? {
        id: newId,
        title: newMessage.title,
        message_content_type: newMessage.message_content_type,
        card_id: newMessage.card_id || newMessage.content_id || newMessage.message_content_params?.card_id
      } : 'NULL')

      // If the message ID hasn't changed, skip processing to avoid unnecessary reloads
      if (oldId && newId && oldId === newId) {
        console.log('Message ID unchanged, skipping reload')
        console.log('=== selectedMessage WATCHER COMPLETED (SKIPPED) ===')
        return
      }

      // Clear card and post data immediately to prevent showing old data
      console.log('Clearing selectedMessageCardData and selectedMessagePostData')
      this.selectedMessageCardData = null
      this.selectedMessagePostData = null

      const isCardNotification = newMessage && (
        newMessage.message_content_type === 'card_share_notification' ||
        newMessage.message_content_type === 'card_remind_notification' ||
        newMessage.message_type === 'card_remind_notification'
      )
      console.log('isCardNotification:', isCardNotification)
      if (isCardNotification) {
        console.log('Calling loadCardData for selectedMessage')
        await this.loadCardData(newMessage, 'selectedMessage')
      }

      const isPostNotification = newMessage && newMessage.message_content_type === 'generative_content_notification'
      console.log('isPostNotification:', isPostNotification)
      if (isPostNotification) {
        console.log('Calling loadPostData for selectedMessage')
        await this.loadPostData(newMessage, 'selectedMessage')
      }
      console.log('=== selectedMessage WATCHER COMPLETED ===')
    }
  },
  mounted() {
    console.log('=== INBOX COMPONENT MOUNTED ===')
    console.log('Calling loadMessages() from mounted hook')
    this.loadMessages()
    // Add global mouse event listeners for resize
    document.addEventListener('mousemove', this.handleResize)
    document.addEventListener('mouseup', this.stopResize)
    document.addEventListener('mousemove', this.handleRightPanelResize)
    document.addEventListener('mouseup', this.stopRightPanelResize)
    // Add window resize listener for responsive behavior
    window.addEventListener('resize', this.handleWindowResize)
  },
  beforeDestroy() {
    // Remove global mouse event listeners
    document.removeEventListener('mousemove', this.handleResize)
    document.removeEventListener('mouseup', this.stopResize)
    document.removeEventListener('mousemove', this.handleRightPanelResize)
    document.removeEventListener('mouseup', this.stopRightPanelResize)
    // Remove window resize listener
    window.removeEventListener('resize', this.handleWindowResize)
  },
  methods: {
    // Sidebar resize methods
    startResize(e) {
      this.isResizing = true
      this.resizeStartX = e.clientX
      this.resizeStartWidth = this.sidebarWidth
      e.preventDefault()
    },
    handleResize(e) {
      if (!this.isResizing) return

      const deltaX = e.clientX - this.resizeStartX
      const newWidth = this.resizeStartWidth + deltaX

      // Set min and max width constraints
      if (newWidth >= 250 && newWidth <= 600) {
        this.sidebarWidth = newWidth
      }
    },
    stopResize() {
      this.isResizing = false
    },

    // Right panel resize methods
    startRightPanelResize(e) {
      this.isResizingRightPanel = true
      this.rightPanelResizeStartX = e.clientX
      this.rightPanelResizeStartWidth = this.rightPanelWidth
      e.preventDefault()
    },
    handleRightPanelResize(e) {
      if (!this.isResizingRightPanel) return

      const deltaX = this.rightPanelResizeStartX - e.clientX
      const newWidth = this.rightPanelResizeStartWidth + deltaX

      // Set min and max width constraints
      if (newWidth >= 300 && newWidth <= 800) {
        this.rightPanelWidth = newWidth
      }
    },
    stopRightPanelResize() {
      this.isResizingRightPanel = false
    },

    async loadMessages(reset = true) {
      console.log('=== loadMessages CALLED ===')
      console.log('reset:', reset)
      this.isLoadingMessages = true
      try {
        if (reset) {
          this.hasMoreMessagesForward = true
          this.hasMoreMessagesBackward = true
        }

        const params = {
          user_id: getUserId(),
          limit: this.messagesLimit,
          read_status: 'all'
        }

        // First load: no direction or line_time, backend will auto-detect

        const response = await postRequest('getMyConversationMessages', params)

        if (response.data.code === 0) {
          const newMessages = response.data.data.messages || response.data.data || []
          console.log('Loaded', newMessages.length, 'messages from backend')
          console.log('First 3 messages:', newMessages.slice(0, 3).map(m => ({
            id: m._id?.$oid || m._id?.$id || m.id,
            title: m.title,
            message_type: m.message_type,
            message_content_type: m.message_content_type,
            card_id: m.card_id || m.content_id || m.message_content_params?.card_id
          })))

          if (reset) {
            this.messages = newMessages
          } else {
            this.messages = [...this.messages, ...newMessages]
          }

          this.unreadCount = response.data.data.unread_count || 0

          // Check if there are more messages to load
          if (newMessages.length < this.messagesLimit) {
            this.hasMoreMessagesForward = false
            this.hasMoreMessagesBackward = false
          }

          // Auto-select first message when loading fresh messages
          if (reset && this.messages.length > 0) {
            console.log('=== AUTO-SELECTING FIRST MESSAGE ===')
            console.log('Message to auto-select:', {
              id: this.messages[0]._id?.$oid || this.messages[0]._id?.$id || this.messages[0].id,
              title: this.messages[0].title,
              message_content_type: this.messages[0].message_content_type,
              card_id: this.messages[0].card_id || this.messages[0].content_id || this.messages[0].message_content_params?.card_id
            })
            this.$nextTick(() => {
              this.selectMessage(this.messages[0])
            })
          } else {
            console.log('Not auto-selecting. reset:', reset, 'messages.length:', this.messages.length)
          }
        }
      } catch (error) {
        console.error('Failed to load messages:', error)
        this.$message.error('Failed to load messages')
      } finally {
        this.isLoadingMessages = false
      }
    },


    async loadMoreMessagesForward() {
      if (this.isLoadingMoreMessagesForward || !this.hasMoreMessagesForward) return

      // Get the latest message's send_time and id
      if (this.messages.length === 0) return
      const latestMessage = this.messages[this.messages.length - 1]
      const line_time = latestMessage.send_time || latestMessage.create_time
      const line_id = latestMessage._id?.$oid || latestMessage._id?.$id || latestMessage.id

      // Store current scroll position
      const messageList = document.querySelector('.message-list')
      const oldScrollHeight = messageList ? messageList.scrollHeight : 0
      const oldScrollTop = messageList ? messageList.scrollTop : 0

      this.isLoadingMoreMessagesForward = true
      try {
        const response = await postRequest('getMyConversationMessages', {
          user_id: getUserId(),
          limit: this.messagesLimit,
          read_status: 'all',
          direction: 'forward',
          line_time: line_time,
          line_id: line_id
        })

        if (response.data.code === 0) {
          const newMessages = response.data.data.messages || response.data.data || []

          // Append to the end
          this.messages = [...this.messages, ...newMessages]

          this.unreadCount = response.data.data.unread_count || 0

          // Check if there are more messages to load forward
          if (newMessages.length < this.messagesLimit) {
            this.hasMoreMessagesForward = false
          }

          // Maintain scroll position after adding new messages to the bottom
          this.$nextTick(() => {
            if (messageList) {
              const newScrollHeight = messageList.scrollHeight
              const scrollDiff = newScrollHeight - oldScrollHeight
              messageList.scrollTop = oldScrollTop + scrollDiff
            }
          })
        }
      } catch (error) {
        console.error('Failed to load more messages:', error)
        this.$message.error('Failed to load more messages')
      } finally {
        this.isLoadingMoreMessagesForward = false
      }
    },

    async loadMoreMessagesBackward() {
      if (this.isLoadingMoreMessagesBackward || !this.hasMoreMessagesBackward) return 0

      // Get the earliest message's send_time and id
      if (this.messages.length === 0) return 0
      const earliestMessage = this.messages[0]
      const line_time = earliestMessage.send_time || earliestMessage.create_time
      const line_id = earliestMessage._id?.$oid || earliestMessage._id?.$id || earliestMessage.id

      console.log('=== loadMoreMessagesBackward ===')
      console.log('Loading messages BEFORE line_time:', line_time, 'line_id:', line_id)
      console.log('Current messages count:', this.messages.length)

      // Store current scroll position
      const messageList = document.querySelector('.message-list')
      const oldScrollHeight = messageList ? messageList.scrollHeight : 0
      const oldScrollTop = messageList ? messageList.scrollTop : 0

      this.isLoadingMoreMessagesBackward = true
      try {
        const response = await postRequest('getMyConversationMessages', {
          user_id: getUserId(),
          limit: this.messagesLimit,
          read_status: 'all',
          direction: 'backward',
          line_time: line_time,
          line_id: line_id
        })

        if (response.data.code === 0) {
          const newMessages = response.data.data.messages || response.data.data || []
          console.log('Received', newMessages.length, 'new messages from backend')

          // Remove duplicates - check if any new messages already exist in current messages
          const existingIds = new Set(this.messages.map(m => m._id?.$oid || m._id?.$id || m.id))
          const uniqueNewMessages = newMessages.filter(m => {
            const id = m._id?.$oid || m._id?.$id || m.id
            if (existingIds.has(id)) {
              console.warn('Duplicate message detected and removed:', id, m.title)
              return false
            }
            return true
          })
          console.log('After removing duplicates:', uniqueNewMessages.length, 'unique messages')

          // Prepend to the beginning
          this.messages = [...uniqueNewMessages, ...this.messages]
          console.log('Total messages after prepending:', this.messages.length)

          this.unreadCount = response.data.data.unread_count || 0

          // Check if there are more messages to load backward
          if (newMessages.length < this.messagesLimit) {
            this.hasMoreMessagesBackward = false
          }

          // Maintain scroll position after adding new messages to the top
          this.$nextTick(() => {
            if (messageList) {
              const newScrollHeight = messageList.scrollHeight
              const scrollDiff = newScrollHeight - oldScrollHeight
              messageList.scrollTop = oldScrollTop + scrollDiff
            }
          })

          return uniqueNewMessages.length
        }
      } catch (error) {
        console.error('Failed to load earlier messages:', error)
        this.$message.error('Failed to load earlier messages')
      } finally {
        this.isLoadingMoreMessagesBackward = false
      }
      return 0
    },

    async refreshMessages() {
      this.isRefreshing = true
      try {
        // Reload messages from scratch (backend will auto-detect starting point)
        await this.loadMessages(true)

        this.$message.success('Messages refreshed')
      } catch (error) {
        console.error('Failed to refresh messages:', error)
        this.$message.error('Failed to refresh messages')
      } finally {
        this.isRefreshing = false
      }
    },

    closeForcedPanels() {
      this.forceShowLeftSidebar = false
      this.showLeftSidebar = false
      if (this.forceShowRightPanel) {
        this.showRightPanel = false
      }
      this.forceShowRightPanel = false
    },

    handleWindowResize() {
      this.windowWidth = window.innerWidth
    },

    // Navigate to previous/next message in message list (middle panel)
    async navigateToPreviousMessage() {
      console.log('=== Navigation: Previous Message (UP ARROW) ===')
      this.lastNavigationDirection = 'up'
      const currentId = this.selectedMessage?._id?.$oid || this.selectedMessage?._id?.$id || this.selectedMessage?.id
      console.log('Current index:', this.currentMessageIndex, 'of', this.messages.length)
      console.log('Current message ID:', currentId)
      console.log('Current message title:', this.selectedMessage?.title)
      console.log('hasMoreMessagesBackward:', this.hasMoreMessagesBackward)
      console.log('hasMoreMessagesForward:', this.hasMoreMessagesForward)

      if (this.currentMessageIndex > 0) {
        // Navigate to existing previous message
        const prevMessage = this.messages[this.currentMessageIndex - 1]
        console.log('Navigating to existing previous message at index', this.currentMessageIndex - 1)
        console.log('Target message ID:', prevMessage._id?.$oid || prevMessage._id?.$id || prevMessage.id)
        console.log('Target message title:', prevMessage.title)
        await this.selectMessage(prevMessage)
      } else if (this.currentMessageIndex === 0) {
        // At the beginning, trigger load more backward to get earlier messages
        console.log('At beginning of list (index 0), loading earlier messages...')
        if (this.hasMoreMessagesBackward) {
          console.log('BEFORE loading: Current message at index 0, ID:', currentId)
          console.log('First 3 message IDs in array:', this.messages.slice(0, 3).map(m => m._id?.$oid || m._id?.$id || m.id))

          const newMessagesCount = await this.loadMoreMessagesBackward()

          console.log('AFTER loading:', newMessagesCount, 'new unique messages added')
          console.log('Total messages now:', this.messages.length)
          console.log('First 5 message IDs in array:', this.messages.slice(0, 5).map(m => m._id?.$oid || m._id?.$id || m.id))

          // Find where the current message is now
          const newCurrentIndex = this.messages.findIndex(m => (m._id?.$oid || m._id?.$id || m.id) === currentId)
          console.log('Current message now at index:', newCurrentIndex)

          // After loading, select just one message earlier (not the first one)
          // The previously selected message (was at index 0) is now at index newMessagesCount
          // So to move one earlier, select message at index newMessagesCount - 1
          if (newMessagesCount > 0 && this.messages.length > newMessagesCount - 1) {
            const targetIndex = newMessagesCount - 1
            console.log('Selecting message at index', targetIndex)
            console.log('Target message ID:', this.messages[targetIndex]._id?.$oid || this.messages[targetIndex]._id?.$id || this.messages[targetIndex].id)
            console.log('Target message title:', this.messages[targetIndex].title)
            await this.selectMessage(this.messages[targetIndex])
          } else if (newMessagesCount === 0) {
            console.log('WARNING: No new unique messages were added (all were duplicates or none returned)')
            console.log('Staying at current message')
          } else {
            console.log('ERROR: Not enough messages to select previous')
          }
        } else {
          console.log('No more backward messages available')
          this.$message.info('No earlier messages')
        }
      }
      console.log('=== Previous Message Navigation COMPLETE ===')
    },

    async navigateToNextMessage() {
      console.log('=== Navigation: Next Message (DOWN ARROW) ===')
      this.lastNavigationDirection = 'down'
      console.log('Current index:', this.currentMessageIndex, 'of', this.messages.length)
      console.log('Current message ID:', this.selectedMessage?._id?.$oid || this.selectedMessage?._id?.$id || this.selectedMessage?.id)
      console.log('Current message title:', this.selectedMessage?.title)
      console.log('hasMoreMessagesBackward:', this.hasMoreMessagesBackward)
      console.log('hasMoreMessagesForward:', this.hasMoreMessagesForward)

      if (this.currentMessageIndex < 0) {
        console.error('ERROR: currentMessageIndex is negative!', this.currentMessageIndex)
        return
      }

      if (this.currentMessageIndex < this.messages.length - 1) {
        // Navigate to existing next message
        const nextMessage = this.messages[this.currentMessageIndex + 1]
        console.log('Navigating to existing next message at index', this.currentMessageIndex + 1)
        console.log('Target message ID:', nextMessage._id?.$oid || nextMessage._id?.$id || nextMessage.id)
        console.log('Target message title:', nextMessage.title)
        await this.selectMessage(nextMessage)
      } else if (this.currentMessageIndex === this.messages.length - 1) {
        // At the end, trigger load more forward
        console.log('At end of list (index', this.messages.length - 1, '), loading more messages forward...')
        if (this.hasMoreMessagesForward) {
          console.log('Loading more forward messages...')
          await this.loadMoreMessagesForward()
          console.log('After loading, total messages:', this.messages.length)
          // After loading, select the next message (current index + 1)
          if (this.messages.length > this.currentMessageIndex + 1) {
            console.log('Selecting message at index', this.currentMessageIndex + 1)
            await this.selectMessage(this.messages[this.currentMessageIndex + 1])
          } else {
            console.log('ERROR: Not enough messages to navigate forward after loading')
          }
        } else {
          console.log('No more forward messages available')
          this.$message.info('No more messages')
        }
      } else {
        console.error('ERROR: Unexpected state - currentMessageIndex:', this.currentMessageIndex, 'messages.length:', this.messages.length)
      }
      console.log('=== Next Message Navigation COMPLETE ===')
    },

    async removeCurrentMessageAndMoveNext() {
      console.log('=== Removing current message and auto-skipping ===')
      console.log('Last navigation direction:', this.lastNavigationDirection)
      const currentMessageId = this.selectedMessage?._id?.$oid || this.selectedMessage?._id?.$id || this.selectedMessage?.id
      const currentIndex = this.currentMessageIndex

      if (currentIndex < 0 || currentIndex >= this.messages.length) {
        console.error('Invalid current message index')
        return
      }

      // Remove the message from the list
      this.messages.splice(currentIndex, 1)
      console.log(`Removed message at index ${currentIndex}, new list length: ${this.messages.length}`)

      // Navigate based on the last navigation direction
      if (this.messages.length === 0) {
        console.log('No more messages')
        this.selectedMessage = null
        this.selectedMessageId = null
        this.layoutMode = 'message-list'
      } else if (this.lastNavigationDirection === 'up') {
        // User was going UP (to earlier messages), so continue going UP
        if (currentIndex > 0) {
          // Select the previous message (which is now at currentIndex - 1)
          console.log('Continuing UP: Selecting previous message at index', currentIndex - 1)
          await this.selectMessage(this.messages[currentIndex - 1])
        } else if (currentIndex === 0 && this.messages.length > 0) {
          // We removed the first message, select the new first message
          console.log('Removed first message, selecting new first at index 0')
          await this.selectMessage(this.messages[0])
        }
      } else if (this.lastNavigationDirection === 'down') {
        // User was going DOWN (to later messages), so continue going DOWN
        if (currentIndex < this.messages.length) {
          // Select the message that is now at the current index (which was the next message)
          console.log('Continuing DOWN: Selecting next message at index', currentIndex)
          await this.selectMessage(this.messages[currentIndex])
        } else {
          // We were at the end, select the new last message
          console.log('Was at end, selecting new last message at index', this.messages.length - 1)
          await this.selectMessage(this.messages[this.messages.length - 1])
        }
      } else {
        // No direction tracked, default to forward (down)
        console.log('No navigation direction tracked, defaulting to forward')
        if (currentIndex < this.messages.length) {
          console.log('Selecting message at index', currentIndex)
          await this.selectMessage(this.messages[currentIndex])
        } else {
          console.log('Selecting last message at index', this.messages.length - 1)
          await this.selectMessage(this.messages[this.messages.length - 1])
        }
      }
    },

    // Select a message from the message list
    async selectMessage(message) {
      console.log('=== selectMessage CALLED ===')
      console.log('Message to select:', {
        id: message._id?.$oid || message._id?.$id || message.id,
        title: message.title,
        message_content_type: message.message_content_type,
        message_type: message.message_type,
        card_id: message.card_id,
        content_id: message.content_id,
        has_message_content_params: !!message.message_content_params
      })

      // Check if this is a card notification
      const isCardNotification =
        message.message_content_type === 'card_share_notification' ||
        message.message_content_type === 'card_remind_notification' ||
        message.message_type === 'card_remind_notification'

      // If it's a card notification and doesn't have message_content_params, fetch full message details
      if (isCardNotification && !message.message_content_params) {
        console.log('Card notification detected without message_content_params, fetching full message...')
        try {
          const messageId = message._id?.$oid || message._id?.$id || message.id
          const response = await postRequest('getMessage', {
            user_id: getUserId(),
            message_id: messageId
          })

          if (response.data.code === 0 && response.data.data) {
            console.log('Full message fetched from backend')
            // Merge the full message data with the existing message
            message = { ...message, ...response.data.data }
          }
        } catch (error) {
          console.error('Error fetching full message:', error)
        }
      }

      console.log('Setting selectedMessage and selectedMessageId...')
      console.log('This will trigger the selectedMessage watcher')
      this.selectedMessage = message
      this.selectedMessageId = message._id?.$oid || message._id?.$id || message.id
      this.layoutMode = 'message-view'
      this.showRightPanel = true

      console.log('isCardNotification:', isCardNotification)

      // NOTE: We don't call loadCardData here because the watcher will handle it
      // This is to avoid double-loading

      // Mark as read if unread
      const messageId = message._id?.$oid || message._id?.$id || message.id
      if (!message.receiver_read_status) {
        this.markAsRead(messageId)
        message.receiver_read_status = true
        this.unreadCount = Math.max(0, this.unreadCount - 1)
      }

      // Scroll the selected message into view
      this.$nextTick(() => {
        const messageElement = document.querySelector(`[data-message-id="${messageId}"]`)
        if (messageElement) {
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
      console.log('=== selectMessage COMPLETED (watcher should fire next) ===')
    },

    async markAsRead(messageId) {
      try {
        await postRequest('markMessageAsRead', {
          user_id: getUserId(),
          message_id: messageId
        })
      } catch (error) {
        console.error('Failed to mark message as read:', error)
      }
    },

    async deleteMessage(messageId) {
      try {
        await this.$confirm('Are you sure you want to delete this message?', 'Warning', {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        })

        await postRequest('deleteMessage', {
          message_id: messageId,
          user_id: getUserId()
        })

        this.messages = this.messages.filter(m => (m._id?.$oid || m._id?.$id || m.id) !== messageId)
        if (this.selectedMessageId === messageId) {
          this.selectedMessage = null
          this.selectedMessageId = null
          this.layoutMode = null
        }
        this.$message.success('Message deleted')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Failed to delete message:', error)
          this.$message.error('Failed to delete message')
        }
      }
    },

    openPost(postId) {
      this.$router.push({
        path: '/stack',
        query: { id: postId }
      })
    },

    openProfile(userId) {
      this.$router.push({
        path: '/profile',
        query: { user_id: userId }
      })
    },

    openAttachment(attachment) {
      window.open(attachment.url, '_blank')
    },

    getMessagePreview(message) {
      if (message.message_type === 'post') {
        return `Shared a post: ${message.post?.title || 'Untitled'}`
      }
      const text = this.stripHtmlTags(message.content || '')
      // Allow up to 300 characters for 5-line display, CSS will handle truncation with ellipsis
      return text.length > 300 ? text.substring(0, 300) + '...' : text
    },

    getPostPreview(content) {
      if (!content) return ''
      const text = this.stripHtmlTags(content)
      return text.length > 200 ? text.substring(0, 200) + '...' : text
    },

    stripHtmlTags(html) {
      const doc = new DOMParser().parseFromString(html || '', 'text/html')
      return doc.body.textContent || ''
    },

    parseMessageContent(content) {
      if (!content) return ''
      return marked.parse(content)
    },

    formatMessageTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      const now = new Date()
      const diff = now - date

      if (diff < 86400000) { // Less than 24 hours (today)
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      } else { // Yesterday and earlier - show actual date
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
    },

    formatFullTime(timestamp) {
      if (!timestamp) return ''
      return new Date(timestamp * 1000).toLocaleString('en-US')
    },

    getAttachmentIcon(type) {
      const iconMap = {
        'image': 'el-icon-picture',
        'document': 'el-icon-document',
        'video': 'el-icon-video-camera',
        'audio': 'el-icon-headset',
        'default': 'el-icon-paperclip'
      }
      return iconMap[type] || iconMap.default
    },

    getBorderStyleByUserType(userType) {
      const type = userType || 'individual'
      switch(type) {
        case 'individual':
          return '1px solid #1890ff'
        case 'institution':
          return '1px solid #67C23A'
        case 'agent':
          return '1px solid #E6A23C'
        default:
          return '1px solid #e0e0e0'
      }
    },

    isValidAvatar(avatar) {
      return avatar && avatar !== '' && avatar !== 'undefined'
    },

    getInitial(name) {
      return name ? name.charAt(0).toUpperCase() : '?'
    },

    getMessageSender(message) {
      // Try to get sender from message data first
      if (message.sender?.user_nickName) {
        return {
          name: message.sender.user_nickName,
          avatar: message.sender.user_avatar_address_small,
          userType: message.sender.user_type
        }
      }

      // Fallback to selected dialogue's other user info
      if (this.selectedDialogue) {
        return {
          name: this.selectedDialogue.userName || 'Unknown',
          avatar: this.selectedDialogue.userAvatar,
          userType: this.selectedDialogue.userType
        }
      }

      // Last resort fallback
      return {
        name: 'Unknown',
        avatar: null,
        userType: null
      }
    },

    getCardTitle(message) {
      // Extract card title from message content params or title field
      if (message.message_content_params?.card_info?.title) {
        return message.message_content_params.card_info.title
      }
      // Parse from message title if available (e.g., "Knowit Card Reminder: Title")
      if (message.title) {
        const match = message.title.match(/:\s*(.+)$/)
        if (match) {
          return match[1]
        }
      }
      return 'Knowit Card'
    },

    getCardTags(message) {
      // Extract tags from card info
      if (message.message_content_params?.card_info?.labels) {
        return message.message_content_params.card_info.labels
      }
      return []
    },

    getPostTitle(message) {
      // Extract post title from message content params or title field
      if (message.message_content_params?.post_info?.title) {
        return message.message_content_params.post_info.title
      }
      // Parse from message title if available (e.g., "New Content Generated: Title")
      if (message.title) {
        const match = message.title.match(/:\s*(.+)$/)
        if (match) {
          return match[1]
        }
        // If no match, return the whole title
        return message.title
      }
      return 'Post'
    },

    async loadCardData(message, targetProperty) {
      console.log('=== loadCardData CALLED ===')
      console.log('targetProperty:', targetProperty)
      console.log('message:', {
        id: message._id?.$oid || message._id?.$id || message.id,
        title: message.title,
        message_content_type: message.message_content_type,
        card_id: message.card_id,
        content_id: message.content_id,
        message_content_params: message.message_content_params
      })

      // Get card_id from message
      const cardId = message.message_content_params?.card_id || message.card_id || message.content_id
      if (!cardId) {
        console.error('No card_id found in message')
        this.$message.error('Card ID not found in message')
        return
      }

      // Check if we're already loading this card or if it's already loaded
      const currentCardId = this.selectedMessageCardData?.id || this.selectedMessageCardData?._id?.$oid || this.selectedMessageCardData?._id?.$id
      if (targetProperty === 'selectedMessage' && currentCardId === cardId) {
        console.log('Card already loaded, skipping fetch for card_id:', cardId)
        return
      }

      // Track loading state to prevent concurrent loads of the same card
      if (!this._loadingCards) {
        this._loadingCards = new Set()
      }
      if (this._loadingCards.has(cardId)) {
        console.log('Card is already being loaded, skipping duplicate request for card_id:', cardId)
        return
      }

      console.log('Fetching card from backend with card_id:', cardId)
      this._loadingCards.add(cardId)

      // Fetch card data from backend
      try {
        const response = await postRequest('getCard', {
          user_id: getUserId(),
          card_id: cardId
        })

        console.log('getCard response code:', response.data.code)
        if (response.data.code === 0 && response.data.data) {
          console.log('Card loaded successfully:', {
            card_id: response.data.data.id || response.data.data._id?.$oid || response.data.data._id?.$id,
            title: response.data.data.title,
            question: response.data.data.question?.substring(0, 50)
          })
          if (targetProperty === 'selectedMessage') {
            this.selectedMessageCardData = response.data.data
            console.log('Set selectedMessageCardData to card:', response.data.data.id || response.data.data._id?.$oid || response.data.data._id?.$id)
          } else {
            this.selectedDialogueMessageCardData = response.data.data
            console.log('Set selectedDialogueMessageCardData')
          }
        } else {
          console.error('Failed to load card - response code:', response.data.code)
          console.log('Auto-removing broken card and skipping in direction:', this.lastNavigationDirection)
          // Auto-remove the message and continue in the navigation direction
          await this.removeCurrentMessageAndMoveNext()
        }
      } catch (error) {
        console.error('Error loading card data:', error)
        console.log('Auto-removing broken card and skipping in direction:', this.lastNavigationDirection)
        // Auto-remove the message and continue in the navigation direction
        await this.removeCurrentMessageAndMoveNext()
      } finally {
        this._loadingCards.delete(cardId)
      }
      console.log('=== loadCardData COMPLETED ===')
    },

    async loadPostData(message, targetProperty) {
      console.log('=== loadPostData called ===')
      console.log('message.message_content_params:', message.message_content_params)

      // Check if post info is already in message_content_params
      if (message.message_content_params?.post_info) {
        console.log('Post info found in message_content_params')
        const postData = message.message_content_params.post_info

        if (targetProperty === 'selectedMessage') {
          this.selectedMessagePostData = postData
        } else {
          this.selectedDialogueMessagePostData = postData
        }
        return
      }

      // Check for post_id in various locations
      const postId = message.message_content_params?.content_id ||
                     message.message_content_params?.post_id ||
                     message.post_id ||
                     message.content_id
      if (!postId) {
        console.error('No post_id found in message')
        console.log('Full message:', message)
        return
      }

      console.log('Fetching post from backend with ID:', postId)

      // Fetch post data from backend
      try {
        const response = await postRequest('getPost', {
          user_id: getUserId(),
          post_id: postId
        })

        if (response.data.code === 0 && response.data.data) {
          console.log('Post data loaded successfully:', response.data.data)
          const postData = response.data.data
          console.log('collection_title:', postData.collection_title)
          console.log('group_title:', postData.group_title)
          console.log('quiz_count:', postData.quiz_count)

          if (targetProperty === 'selectedMessage') {
            this.selectedMessagePostData = postData
          } else {
            this.selectedDialogueMessagePostData = postData
          }
          console.log('Post data set to:', targetProperty === 'selectedMessage' ? this.selectedMessagePostData : this.selectedDialogueMessagePostData)
        } else {
          console.error('Failed to load post - response:', response.data)
          this.$message.error('Failed to load post')
        }
      } catch (error) {
        console.error('Error loading post data:', error)
        this.$message.error('Failed to load post')
      }
    },

    // =========================================================================
    // Interested Questions Methods | 感兴趣问题方法
    // =========================================================================
    
    async loadInterestedQuestions(reset = true) {
      console.log('=== loadInterestedQuestions called ===')
      
      if (reset) {
        this.isLoadingInterestedQuestions = true
        this.questionsOffset = 0
        this.interestedQuestions = []
      }
      
      try {
        // Call the Python backend API via postRequest (PHP-compatible)
        const response = await postRequest('getUserInterestedQuestions', {
          user_id: getUserId(),
          limit: this.questionsLimit,
          offset: this.questionsOffset
        })
        
        if (response.data.code === 0 && response.data.data) {
          const newQuestions = response.data.data.questions || []
          
          if (reset) {
            this.interestedQuestions = newQuestions
          } else {
            this.interestedQuestions = [...this.interestedQuestions, ...newQuestions]
          }
          
          this.hasMoreQuestions = newQuestions.length >= this.questionsLimit
          console.log('Loaded', newQuestions.length, 'interested questions')
        } else {
          console.error('Failed to load interested questions:', response.data.msg)
        }
      } catch (error) {
        console.error('Error loading interested questions:', error)
      } finally {
        this.isLoadingInterestedQuestions = false
        this.isLoadingMoreQuestions = false
      }
    },
    
    async loadMoreInterestedQuestions() {
      if (this.isLoadingMoreQuestions || !this.hasMoreQuestions) return
      
      this.isLoadingMoreQuestions = true
      this.questionsOffset += this.questionsLimit
      await this.loadInterestedQuestions(false)
    },
    
    selectQuestion(question) {
      this.selectedQuestion = question
      this.showQuestionDetail = true
    },
    
    formatContent(content) {
      if (!content) return ''
      // Simple markdown-like formatting
      return content
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br>')
        .replace(/^/, '<p>')
        .replace(/$/, '</p>')
    }

  }
}
</script>

<style scoped>
.inbox-container {
  height: 100vh;
  width: 100vw;
  max-width: 100vw;
  background-color: #ebebeb;
  overflow: hidden;
  box-sizing: border-box;
  position: relative;
  display: flex;
}

/* Ensure all avatars maintain circular shape and don't shrink */
.inbox-container :deep(.el-avatar) {
  flex-shrink: 0;
  min-width: 40px;
  min-height: 40px;
}

.inbox-container :deep(.el-avatar > img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.inbox-container.is-resizing {
  cursor: col-resize;
  user-select: none;
}

.inbox-container.is-resizing * {
  cursor: col-resize !important;
  user-select: none;
}

.inbox-container.is-resizing-right * {
  cursor: col-resize !important;
  user-select: none;
}

/* When left sidebar is hidden, center the middle panel */
/* Not needed anymore - using non-flexbox layout */

/* Part A: Left Sidebar - Resizable */
.inbox-sidebar {
  min-width: 250px;
  max-width: 600px;
  background: #f5f5f5;
  border-right: 1px solid #d0d0d0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
}

.resize-handle {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  cursor: col-resize;
  z-index: 100;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.resize-handle::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 2px;
  width: 1px;
  background: #e4e7ed;
}

.resize-handle::after {
  content: '';
  width: 4px;
  height: 32px;
  background: #d0d0d0;
  border-radius: 2px;
  position: relative;
  z-index: 1;
  transition: background-color 0.2s;
}

.resize-handle:hover::after {
  background-color: #409eff;
}

.resize-handle-right {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  cursor: col-resize;
  z-index: 1000;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.resize-handle-right::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 2px;
  width: 1px;
  background: #e4e7ed;
}

.resize-handle-right::after {
  content: '';
  width: 4px;
  height: 32px;
  background: #d0d0d0;
  border-radius: 2px;
  position: relative;
  z-index: 1;
  transition: background-color 0.2s;
}

.resize-handle-right:hover::after {
  background-color: #409eff;
}

.inbox-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.inbox-header {
  padding: 12px 16px;
  border-bottom: 1px solid #d0d0d0;
  flex-shrink: 0;
  background: #ebebeb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.inbox-title-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.collapse-btn {
  flex-shrink: 0;
}

.inbox-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.tab-unread-badge {
  margin-left: 8px;
}

.tab-unread-badge :deep(.el-badge__content) {
  transform: translateY(-2px);
}

.message-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.filter-tag {
  cursor: pointer;
  user-select: none;
  transition: all 0.3s;
}

.filter-tag:hover {
  opacity: 0.8;
}

.refresh-btn {
  margin-left: auto;
  color: #606266;
  padding: 4px;
}

.refresh-btn:hover {
  color: #409eff;
}

.message-list,
.dialogue-list {
  flex: 1;
  overflow-y: auto;
}

.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
}

.load-more-container {
  display: flex;
  justify-content: center;
  padding: 16px 0;
  border-top: 1px solid #e0e0e0;
}

.load-more-container.load-more-top {
  border-top: none;
  border-bottom: 1px solid #e0e0e0;
}

.load-more-container .el-button {
  color: #409eff;
  font-size: 14px;
}

.message-item,
.dialogue-item {
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  gap: 12px;
}

.message-item:hover,
.dialogue-item:hover {
  background-color: #ebebeb;
}

.message-item.active,
.dialogue-item.active {
  background-color: #d9ecff;
  border-left: 3px solid #409eff;
}

.message-item.unread {
  font-weight: 500;
}

.dialogue-item.has-unread {
  font-weight: 500;
}

.message-item-header {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.message-info,
.dialogue-info {
  flex: 1;
  min-width: 0;
}

.message-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  gap: 8px;
}

.sender-name,
.dialogue-name {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dialogue-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  gap: 8px;
}

.dialogue-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.message-preview {
  color: #909399;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}

.dialogue-preview {
  color: #909399;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}

.message-meta {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.message-time,
.dialogue-time {
  font-size: 12px;
  color: #c0c4cc;
}

/* Content Panels - Part B and Part C */
.content-panel {
  background: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.middle-panel {
  flex: 1;
  min-width: 0;
  height: 100vh;
  background: #ffffff;
  z-index: 10;
  position: relative;
  border-left: 1px solid #d0d0d0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.right-panel {
  flex: 0 0 auto !important;
  min-width: 300px;
  max-width: 800px;
  flex-shrink: 0;
  transition: none;
  position: relative;
  border-left: 1px solid #d0d0d0;
  overflow: hidden;
  background: #f5f5f5;
}

.right-panel.collapsed {
  width: 50px !important;
  min-width: 50px;
  max-width: 50px;
}

.secondary-panel .message-detail-header,
.secondary-panel .conversation-header {
  background: #ebebeb;
}

.secondary-panel .conversation-messages {
  background: #f0f0f0;
}

.secondary-panel .message-detail-content {
  background: #f0f0f0;
}

.expand-btn {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #909399;
  font-size: 18px;
  transition: all 0.3s;
}

.expand-btn:hover {
  background: #f5f7fa;
  color: #409eff;
}

.panel-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.right-panel .panel-content {
  margin-left: 5px;
  width: calc(100% - 5px);
  background: white;
  height: 100%;
}

.message-detail {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: visible;
  position: relative;
}

.message-navigation {
  position: fixed;
  bottom: 20px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
  z-index: 100;
}

.message-navigation .el-button--small {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  padding: 9px !important;
  border-radius: 4px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: #ecf5ff !important;
  border-color: #b3d8ff !important;
  color: #409eff !important;
}

.message-navigation .el-button--small:hover {
  background: #409eff !important;
  border-color: #409eff !important;
  color: white !important;
}

.message-detail-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  position: relative;
  background: #fafafa;
}

.sender-info {
  display: flex;
  align-items: center;
}

.sender-info h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
}

.message-timestamp {
  font-size: 13px;
  color: #909399;
}

.message-actions {
  display: flex;
  gap: 8px;
}

.message-detail-content {
  flex: 1;
  padding: 20px;
  padding-bottom: 120px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.message-content-text {
  font-size: 15px;
  line-height: 1.8;
  color: #303133;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-width: 600px;
  width: 100%;
}

.card-share-content {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.post-share-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

.post-share-content .message-content-text {
  width: 100%;
  max-width: 700px;
  text-align: left;
}

.post-share-content :deep(.post-preview) {
  width: 100%;
  max-width: 700px;
}

.post-share-content :deep(.direct-read-post) {
  width: 100%;
}

.loading-card,
.loading-post {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  gap: 12px;
}

.loading-card i,
.loading-post i {
  font-size: 32px;
}

.post-message {
  margin-top: 16px;
}

.post-card {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s;
}

.post-card:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.post-card h4 {
  margin: 8px 0;
  font-size: 16px;
  color: #303133;
}

.post-content {
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
  margin: 12px 0;
}

.post-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  font-size: 13px;
  color: #909399;
}

/* Conversation Panel */
.conversation-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e4e7ed;
  flex-shrink: 0;
  position: relative;
  background: #fafafa;
}

.conversation-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  padding-right: 40px;
}

.collapse-btn {
  position: absolute;
  top: 12px;
  right: 16px;
  font-size: 18px;
  color: #909399;
  padding: 4px;
}

.collapse-btn:hover {
  color: #409eff;
}

.conversation-messages {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fafafa;
}

.loading-more-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  color: #909399;
  font-size: 14px;
}

.loading-more-indicator i {
  font-size: 16px;
}

.conversation-message-wrapper {
  display: flex;
  justify-content: flex-start;
  width: 100%;
}

.conversation-message-wrapper.sent-by-me {
  justify-content: flex-end;
}

.conversation-message {
  max-width: 70%;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.conversation-message:hover {
  background: #f5f7fa;
}

/* Only show selection highlight when right panel is visible */
.inbox-container.right-panel-visible .conversation-message.selected {
  box-shadow: 0 0 0 2px #409eff;
}

.conversation-message-wrapper.sent-by-me .conversation-message {
  background: #e1f0ff;
  border: 1px solid #91d5ff;
  border-radius: 12px 12px 0 12px;
}

.conversation-message-wrapper:not(.sent-by-me) .conversation-message {
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 12px 12px 12px 0;
}

.conversation-message-wrapper.sent-by-me .conversation-message:hover {
  background: #d0e7ff;
}

.conversation-message-wrapper:not(.sent-by-me) .conversation-message:hover {
  background: #f5f7fa;
}

.conv-msg-content {
  font-size: 14px;
  color: #303133;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
}

.conv-msg-time {
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
}

.conversation-message-wrapper.sent-by-me .conv-msg-time {
  align-self: flex-end;
}

.conversation-message-wrapper:not(.sent-by-me) .conv-msg-time {
  align-self: flex-start;
}

.conversation-reply {
  padding: 16px;
  border-top: 1px solid #e4e7ed;
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.conversation-reply .el-textarea {
  flex: 1;
}

/* Empty state - shown in detail area when no message selected */
.empty-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border-left: 1px solid #d0d0d0;
  gap: 16px;
  padding: 40px;
  overflow: hidden;
}

/* 当显示问题列表时，允许滚动 */
.empty-content.interested-questions-container,
.empty-content.messages-primary-container {
  overflow-y: auto;
  overflow-x: hidden;
}

.messages-primary-header {
  padding: 20px 24px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.messages-primary-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.messages-primary-title i {
  margin-right: 8px;
  color: #409eff;
}

.messages-primary-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.messages-primary-actions .filter-tag {
  cursor: pointer;
}

.messages-primary-list {
  padding: 0 16px 16px;
}

.message-primary-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
  position: relative;
}

.message-primary-item:hover {
  background: #f0f7ff;
}

.message-primary-item.unread {
  background: #f5f7fa;
}

.message-primary-info {
  flex: 1;
  min-width: 0;
}

.message-primary-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.message-primary-sender {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}

.message-primary-time {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}

.message-primary-preview {
  font-size: 13px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-primary-dot {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

.empty-state-icon {
  width: 160px;
  height: 160px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.empty-state-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.empty-state-text {
  margin: 0;
  font-size: 14px;
  color: #909399;
  text-align: center;
  max-width: 400px;
  line-height: 1.6;
}

/* Responsive */

/* Below 780px: hide left sidebar, detail panel takes full width */
@media (max-width: 779px) {
  .inbox-sidebar {
    display: none !important;
  }

  .middle-panel,
  .empty-content {
    width: 100% !important;
    border-left: none !important;
  }
}

/* Mobile: Adjust for narrow screens */
@media (max-width: 499px) {
  .inbox-sidebar {
    display: none !important;
  }

  .middle-panel,
  .empty-content {
    width: 100% !important;
    border-left: none !important;
  }

  .message-detail-header {
    padding: 12px;
  }

  .message-detail-content {
    padding: 12px;
  }

  .card-share-content {
    max-width: 100%;
    padding: 0 12px;
  }

  .message-content-text {
    font-size: 14px;
  }
}

/* Panel overlay/backdrop */
.panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Floating toggle buttons */
.floating-sidebar-toggle {
  position: fixed;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.floating-sidebar-toggle.left {
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.floating-sidebar-toggle.left:hover {
  transform: translateY(-50%) scale(1.1);
}

.floating-sidebar-toggle.right {
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.floating-sidebar-toggle.right:hover {
  transform: translateY(-50%) scale(1.1);
}

/* Panel close button */
.panel-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 100;
  background: white;
  border: 1px solid #dcdfe6;
}

.panel-close-btn:hover {
  background: #f5f7fa;
}

/* Force show panels (override responsive hiding) */
.inbox-sidebar.force-show,
.right-panel.force-show {
  display: flex !important;
  position: fixed !important;
  height: 100vh;
  top: 0;
  z-index: 999;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  background: white;
  animation: slideIn 0.3s ease;
}

.inbox-sidebar.force-show {
  left: 0;
}

.right-panel.force-show {
  right: 0;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.right-panel.force-show {
  animation: slideInRight 0.3s ease;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Interested Questions Styles */
.interested-questions-container {
  padding: 40px 20px;
  overflow-y: auto !important;
  overflow-x: hidden;
  align-items: stretch !important;
  justify-content: flex-start !important;
  height: 100vh;
  max-height: 100vh;
}

.interested-questions-header {
  text-align: center;
  margin-bottom: 32px;
}

.interested-questions-title {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.interested-questions-title i {
  color: #f7ba2a;
  font-size: 32px;
}

.interested-questions-subtitle {
  color: #909399;
  font-size: 14px;
  margin: 0;
}

.loading-questions,
.no-questions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #909399;
}

.loading-questions i,
.no-questions i {
  font-size: 48px;
  margin-bottom: 16px;
}

.no-questions-hint {
  font-size: 13px;
  color: #c0c4cc;
  text-align: center;
  max-width: 300px;
}

.questions-list {
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
}

.question-card {
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.question-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border-color: #409eff;
  transform: translateY(-2px);
}

.question-card.has-answer {
  border-left: 4px solid #67c23a;
}

.question-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.topic-tag {
  font-size: 12px;
}

.quality-score {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #f7ba2a;
  font-weight: 600;
}

.quality-score i {
  font-size: 14px;
}

.question-text {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  line-height: 1.5;
  margin-bottom: 12px;
}

.answer-preview {
  background: #f0f9eb;
  border-radius: 8px;
  padding: 12px;
  margin-top: 12px;
}

.answer-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #67c23a;
  font-weight: 600;
  margin-bottom: 8px;
}

.answer-snippet {
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
  margin: 0;
}

.pending-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #909399;
  margin-top: 8px;
}

.pending-label i {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.load-more-questions {
  text-align: center;
  padding: 20px 0;
}

/* Question Detail Dialog */
.question-detail-dialog :deep(.el-dialog__header) {
  padding: 20px 24px;
  border-bottom: 1px solid #e4e7ed;
}

.question-detail-dialog :deep(.el-dialog__title) {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.question-detail-dialog :deep(.el-dialog__body) {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.question-meta {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}

.answer-content h4 {
  font-size: 14px;
  color: #909399;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.answer-text {
  font-size: 15px;
  line-height: 1.8;
  color: #303133;
}

.answer-text p {
  margin: 0 0 16px 0;
}

.no-answer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
}

.no-answer i {
  font-size: 32px;
  margin-bottom: 12px;
}

</style>
